/* Copyright(c) 2026 APSS Media Pvt. Ltd.
*  All Rights Reserved
*/
/**
 * @file Modigie-5879 
 * @author  Chaitanya Shinde
 * @section DESCRIPTION Modigie Integration-5879-UI (Excel Upload + Job Type Selection + Preview)
*/

import React from "react";
import Footer from "../layouts/footer";
import { Table, Radio, Button, Upload, Alert, Tooltip } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { css } from "emotion";

import { fetch_custom as fetch } from "../../configration/fetch_default_headers";
import Navigation from "../layouts/VerifierNavPage";
import { logoutUser } from "../login/authentication";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { DatePicker } from 'antd';

const xlsx = require("xlsx");
const Swal = require('sweetalert2');

const lastBatchCardCSS = css`
  background: linear-gradient(to right, #f4f6ff, #ffffff);
  border: 1px solid #d6d9ff;
  border-left: 6px solid #007cc3;
  border-radius: 6px;
  padding: 14px 18px;
  margin-bottom: 16px;

  .label {
    font-weight: 600;
    color: #007cc3;
    margin-right: 4px;
  }

  .value {
    color: #333;
  }

  .status {
    font-weight: 600;
  }
`;

const historyTableCSS = css`
  background-color: white;
  
  .ant-table {
    border-radius: 6px;
  }

  .ant-table-thead > tr > th {
    background-image: linear-gradient(to right, #28166f, #007cc3) !important;
    color: #ffffff !important;
    text-align: center !important;
    font-weight: 500;
    border: 1px solid #e0e0e0;
  }

  .ant-table-tbody > tr > td {
    text-align: center !important;
    border: 1px solid #e0e0e0;
    padding: 6px 8px;
  }
    
`;

const processBtnDisabledCSS = css`
    &&& {
        background-color: #b5b5b5 !important;
        color: #ffffff !important;
        border: none !important;
        cursor: not-allowed !important;
        opacity: 1 !important;  /* fix faded look */
    }
`;

const processBtnCSS = css`
    background-image: linear-gradient(to right,#28166f,#007cc3);
    color: #ffffff !important;
    border: none !important;
`;

const auditBtnCSS = css`
    background-image: linear-gradient(to right,#007cc3,#28166f);
    color: #ffffff !important;
    border: none !important;
`;

const ellipsisText = css`
  display: inline-block;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
`;

const tableEllipsis = css`
  display: inline-block;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
`;

const JOB_REQUIREMENTS = {
  verifyEmployment: {
    label: 'Verify Employment',
    requiredColumns: ['first_name', 'last_name', 'company_name'],
    requiredMessage:
      'Excel must contain First Name, Last Name, Company Name and Prospect LinkedIn URL'
  },
  enrichMobile: {
    label: 'Enrich Mobile & Verify Employment',
    requiredColumns: ['first_name', 'last_name', 'company_name'],
    requiredMessage:
      'Excel must contain First Name, Last Name, Company Name and Prospect LinkedIn URL'
  }
};

class Modigie extends React.Component {
    constructor() {
        super();
        this.state = {
            file: null,
            jobType: 'enrichMobile',
            previewData: [],
            totalRecords: 0,
            validRecords: 0,
            invalidRecords: 0,
            uploadError: null,
            batchStatus: null,
            batchId: null,
            progress: 0,
            lastFileName: null,
            lastJobType: null,
            lastRecordCount: 0,
            batchHistory: [],
            isProcessingClicked: null,
            quota: null,
            estimate: null,
            historyExportFrom: null,
            historyExportTo: null,
            historyExportLoading: false,
        }
    }

    safeFetch = async (url, options = {}) => {
        try {
            const res = await fetch(url, options);

            if (!res.ok) {
                throw new Error('Request failed');
            }

            return res;
        } catch (err) {
            console.error('API error:', err);

            Swal.fire({
                title: 'Server temporarily unavailable',
                text: 'Please try again in a few seconds',
                icon: 'error',
                confirmButtonText: 'Ok'
        })
        // .then(() => {
        //     window.location.reload();
        // });
            throw err;
        }
    };

    fetchQuota = async (jobType) => {
    const res = await fetch(`/modigie/quota/${jobType}`);
    const data = await res.json();
    this.setState({ quota: data });
    };

    fetchEstimate = async (jobType, records) => {
    const res = await fetch(`/modigie/estimate/${jobType}/${records}`);
    const data = await res.json();
    this.setState({ estimate: data });
    };

    componentDidMount() {
        this.restoreLastBatch();
        this.loadBatchHistory();
        this.fetchQuota(this.state.jobType);
    }

    restoreLastBatch = async () => {
    const res = await this.safeFetch('/modigie/status/latest');
    const data = await res.json();

    // console.log('Latest batch from backend:', data);
    
    if (data && data.batchId) {
        this.setState({
        batchId: data.batchId,
        batchStatus: data.status,
        lastFileName: data.fileName,
        lastJobType: data.jobType,
        lastRecordCount: data.recordCount
        });

        this.startStatusPolling(data.batchId);
    }
    };

    loadBatchHistory = async () => {
        try {
            const res = await this.safeFetch('/modigie/history');
            const data = await res.json();

            this.setState({
                batchHistory: data || [],
                noHistoryMsg: (data && data.length === 0)
                    ? "No Batch history available yet."
                    : null
            });

        } catch (err) {
            console.error("History load failed", err);
        }
    };

    /** Handle Excel Upload */
    
    handleFileUpload = (file) => {

        const uploadedName = file.name.trim().toLowerCase();

        const isProcessing =
        this.state.batchHistory.some(
            b => b.status === "processing" && b.file_name.trim().toLowerCase() === uploadedName
        ) ||
        (
            this.state.batchStatus === "processing" &&
            (this.state.lastFileName || "").trim().toLowerCase() === uploadedName
        );

        if (isProcessing) {
        Swal.fire({
            icon: "warning",
            title: "File already processing",
            text: `The file "${file.name}" is already being processed.`
        });
        return false;
        }

        this.setState({ 
            file,
            processingFileName: null   // reset lock when new file uploaded
         });

        const reader = new FileReader();

        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = xlsx.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = xlsx.utils.sheet_to_json(sheet, { defval: '' });

            // Empty Excel file check
            if (!jsonData.length) {
                this.setState({ uploadError: 'Excel file is empty' });
                return;
            }

            const HEADER_ALIASES = {
                firstname: 'first_name',
                first_name: 'first_name',
                "first name": 'first_name',

                lastname: 'last_name',
                last_name: 'last_name',
                "last name": 'last_name',

                company: 'company_name',
                companyname: 'company_name',
                company_name: 'company_name',
                "company name": 'company_name',

                linkedin: 'linkedin_url',
                linkedinlink: 'linkedin_url',
                linkedin_link: 'linkedin_url',
                linkedinurl: 'linkedin_url',
                linkedin_url: 'linkedin_url',
                "linkedin link": 'linkedin_url',

                phone: 'phone'
            };

            // Normalize headers
            // const normalizeKey = (key) =>
            //     key.toString().trim().toLowerCase().replace(/\s+/g, "_");
            const normalizeKey = (key) => {
            const cleaned = key.toString().trim().toLowerCase();
            const compact = cleaned.replace(/\s+/g, '');

            return HEADER_ALIASES[compact] ||
                    HEADER_ALIASES[cleaned] ||
                    cleaned.replace(/\s+/g, "_");
            };

            const normalizedData = jsonData.map(row => {
                const newRow = {};
                Object.keys(row).forEach(k => {
                    newRow[normalizeKey(k)] = row[k];
                });
                return newRow;
            });

            // Required columns
            const requiredColumns = JOB_REQUIREMENTS[this.state.jobType].requiredColumns;
            const excelColumns = Object.keys(normalizedData[0] || {});
            const missing = requiredColumns.filter(col => !excelColumns.includes(col));

            if (missing.length > 0) {
                this.setState({
                    uploadError: `Missing mandatory column(s) for ${ JOB_REQUIREMENTS[this.state.jobType].label } : ${missing.join(', ')}`,
                    previewData: [],
                    fullData: [],
                    totalRecords: 0,
                    validRecords: 0,
                    invalidRecords: 0
                });
                return;
            }

            const seenLinkedIn = new Set();
            // validate all rows
            let validatedRows = normalizedData.map((r, index) => {

                const phone = String(r.phone || "").trim();

                const isPhoneValid =
                    phone === "" ||
                    /^[0-9+\-\s()]{8,15}$/.test(phone);
                    
                const linkedin = String(r.linkedin_url || "")
                                .trim()
                                .toLowerCase()
                                .replace(/^https?:\/\//, "")
                                .replace(/^www\./, "");

                const hasIdentity = Boolean(linkedin); //linkedin mandatory for enrichMobile and verify employment
                
                const isLinkedInValid = linkedin.includes("linkedin.com");

                let errorReason = null;

                if (!r.first_name) errorReason = "First Name required";
                else if (!r.last_name) errorReason = "Last Name required";
                else if (!r.company_name) errorReason = "Company Name required";
                else if (!hasIdentity) errorReason = "LinkedIn URL required";
                else if (!isLinkedInValid) errorReason = "Invalid LinkedIn URL";
                else if (seenLinkedIn.has(linkedin)) errorReason = "Duplicate LinkedIn URL in file";
                else if (!isPhoneValid) errorReason = "Invalid Phone Number";

                if (linkedin && !seenLinkedIn.has(linkedin)) {
                    seenLinkedIn.add(linkedin);
                }

                return {
                    ...r,
                    normalizedLinkedin: linkedin,
                    isValid: !errorReason,
                    errorReason
                };
            });

            const linkedinList = validatedRows
                .map(r => r.normalizedLinkedin)
                .filter(l => l);

            if (linkedinList.length > 0) {

                const res = await this.safeFetch('/modigie/check-duplicates', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ linkedins: linkedinList })
                });

            const data = await res.json();

                if (data.duplicates && data.duplicates.length > 0) {

                    validatedRows = validatedRows.map(r => {

                        if (data.duplicates.includes(r.normalizedLinkedin) && r.isValid) {
                            return {
                                ...r,
                                isValid: false,
                                errorReason: "LinkedIn URL Already Processed Earlier"
                            };
                        }
                        return r;
                    });
                }
            }
            const valid = validatedRows.filter(r => r.isValid).length;
            const invalid = validatedRows.length - valid;

            // SINGLE setState with all variables in scope
            this.setState({
                previewData: validatedRows,
                fullData: validatedRows,   // used for backend upload
                totalRecords: validatedRows.length,
                validRecords: valid,
                invalidRecords: invalid,
                uploadError: null
            });
            this.fetchEstimate(this.state.jobType, this.state.totalRecords);
        };

        reader.readAsArrayBuffer(file);
        return false; // Prevent auto-upload
    };

    /** Start Modigie Processing */

    handleProcess = async () => {
    
    if (this.state.processingFileName === this.state.file.name) {
        return;   // prevent double processing for same file
    }

    this.setState({ processingFileName: this.state.file.name });

    try {
        const reader = new FileReader();
        reader.onload = async () => {
        const base64File = reader.result.split(',')[1];

        const payload = {
            fileName: this.state.file.name,
            fileBase64: base64File,
            jobType: this.state.jobType,
            rows: this.state.fullData
        };

        const res = await this.safeFetch('/modigie/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await res.json();

        if (!data.batchId) {
            Swal.fire('Upload failed', '', 'error');
            return;
        }

        /* STORE batchId + disable process button */
        this.setState({
            batchId: data.batchId,
            batchStatus: 'processing'
        });

        await this.safeFetch(`/modigie/process/${data.batchId}`, {
            method: 'POST'
        });

        // Swal.fire('Processing Started', 'Batch submitted to Modigie', 'success');
        Swal.fire({
            title: 'Processing Started',
            text: 'Batch submitted to Modigie',
            icon: 'success'
            }).then(() => {

            // this.fetchLatestBatch();
            this.fetchQuota(this.state.jobType);
            });

        this.startStatusPolling(data.batchId);
        };

        reader.readAsDataURL(this.state.file);
    } catch (err) {
        Swal.fire('Error', 'Something went wrong', 'error');
    }
    };

    startStatusPolling = (batchId) => {
    this.poller = setInterval(async () => {
        const res = await this.safeFetch(`/modigie/status/${batchId}`);
        const data = await res.json();

        const total = data.stats.total || 1;
        const done = Number(data.stats.completed) + Number(data.stats.failed);
        const percent = Math.round((done / total) * 100);

        this.setState({
        batchStatus: data.batch.status,
        progress: percent,
        batchData: data.batch,
        stats: data.stats
        });

        // FINALIZE ONCE
        if (data.batch.status === 'completed') {
        clearInterval(this.poller);

        // Refresh UI from DB (single source of truth)
        await this.restoreLastBatch();
        await this.loadBatchHistory();
        }
    }, 5000);
    };

    /** Handle Audit Report */
    handleAuditReport = () => {
        const XLSX = require("xlsx");

        let data = this.state.previewData.length
            ? [...this.state.previewData]
            : [];

        if (data.length === 0) {
            Swal.fire("No data to export", "", "warning");
            return;
        }

        /* Remove internal column */
        data = data.map(r => {
            const { normalizedLinkedin, ...rest } = r;
            return {
                "First Name": rest.first_name || "",
                "Last Name": rest.last_name || "",
                "Company Name": rest.company_name || "",
                "LinkedIn URL": rest.linkedin_url || "",
                "Status": rest.isValid ? "Valid" : "Invalid",
                "Error Reason": rest.errorReason || ""
            };
        });
        // Create sheet
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Apply red highlight for invalid rows
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        for (let row = 1; row <= range.e.r; row++) {

            const statusCellRef = XLSX.utils.encode_cell({ r: row, c: 4 });
            const statusCell = worksheet[statusCellRef];

            if (statusCell && statusCell.v === "Invalid") {

                /* Row background light red */
                for (let col = 0; col <= range.e.c; col++) {
                    const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                    if (!worksheet[cellRef]) continue;

                    worksheet[cellRef].s = {
                        fill: { fgColor: { rgb: "FFE6E6" } },
                        font: { color: { rgb: "FF0000" } }
                    };
                }

            }
            else if (statusCell && statusCell.v === "Valid") {

                /* Only status text green */
                statusCell.s = {
                    font: { color: { rgb: "008000" }, bold: true }
                };
            }
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Report");

        XLSX.writeFile(workbook, "Modigie_Audit_Report.xlsx");

        Swal.fire("Audit Report Generated", "Please open the Excel file for corrections.", "success");
    };

    downloadOutput = async (batchId) => {
    try {
        const response = await this.safeFetch(`/modigie/download/${batchId}`);

        if (!response.ok) {
        throw new Error('File not ready');
        }

        const blob = await response.blob();

        // Extract filename from response headers
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = 'Modigie_Output.xlsx';

        if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match && match[1]) {
            fileName = match[1];
        }
        }

        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (err) {
        console.error(err);
        Swal.fire('Download failed', 'Output file not ready yet', 'error');
    }
    };

    getJobTypeLabel = (type) => {
        if (type === "enrichMobile") return "Enrich Mobile & Verify Employment";
        if (type === "verifyEmployment") return "Verify Employment";
        return type;
    };

    downloadHistoryExport = async () => {
        const { historyExportFrom, historyExportTo } = this.state;

        if (!historyExportFrom || !historyExportTo) {
            Swal.fire("Please select date range", "", "warning");
            return;
        }

        try {
            this.setState({ historyExportLoading: true });

            const from = historyExportFrom.format("YYYY-MM-DD");
            const to = historyExportTo.format("YYYY-MM-DD");

            const res = await fetch(
                `/modigie/export-history?from=${from}&to=${to}`
            );

            if (!res.ok) throw new Error("Download failed");

            const blob = await res.blob();

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `Historical_Enriched_Data_${from}_${to}.zip`;
            a.click();

            window.URL.revokeObjectURL(url);

        } catch (e) {
            Swal.fire("No data to export", "", "warning");
        }

        this.setState({ historyExportLoading: false });

    };

    render() {

        const batch = this.state.batchData  || {};
        const stats = this.state.stats  || {};

        const retryCount = Number(batch.retry_count || 0);
        const completed_jobs = Number(stats.completed || 0);
        const failed_jobs = Number(stats.failed || 0);
        const total_jobs = Number(stats.total || 0);
        const processing_jobs = total_jobs - completed_jobs - failed_jobs;

        let batchHealth = "HEALTHY";
        
        if (batch.status === "processing") {

        if (processing_jobs > 0) {

        const createdTime = new Date(batch.created);
        const diffHours = (Date.now() - createdTime) / (1000*60*60);

        if (diffHours > 3) {
            batchHealth = "DELAYED";
        } else {
            batchHealth = "PROCESSING";
        }
       }

       if (failed_jobs > 0) {
        batchHealth = "RECOVERING";
       }
       }
       if (batch.status === "completed") {

       if (failed_jobs > 0) {
        batchHealth = "CRITICAL";
       } else {
        batchHealth = "HEALTHY";
       }
       }
        
        const historyColumns = [
        {
            title: 'File Name',
            dataIndex: 'file_name',
            align: 'center',
            width: 200,
            render: (fileName) => (
                <Tooltip title={fileName}>
                    <span className={tableEllipsis}>
                        {fileName}
                    </span>
                </Tooltip>
            )
        },
        {
            title: 'Job Type',
            dataIndex: 'job_type',
            align: 'center',
            width: 200,
            render: jt =>
            jt === 'verifyEmployment'
                ? 'Verify Employment'
                : 'Enrich Mobile & Verify Employment'
        },
        {
            title: 'Records',
            dataIndex: 'record_count',
            align: 'center',
            width: 100,
            render: (_, row) => {
                const total = Number(row.total_jobs || 0);
                const completed = Number(row.completed_jobs || 0);
                const processing = Number(row.processing_jobs || 0);
                const failed =
                    Number(row.failed_jobs || 0) +
                    Number(row.rejected_jobs || 0);
            return (
            <div style={{ lineHeight: '16px'}}>
                <div style={{ fontWeight: 600 }}>
                    {row.record_count}
                </div>

                <div
                    style={{
                        borderTop: '2px solid #d9d9d9',
                        margin: '6px 0'
                    }}
                />
                <Tooltip
                    title={
                        <div style={{ fontSize: 12 }}>
                            <div>Completed : {completed}</div>
                            <div>Processing : {processing}</div>
                            <div>Failed / Rejected : {failed}</div>
                        </div>
                    }
                >
                    <div style={{ fontSize: 12,fontWeight: 600,cursor:'pointer' }}>
                        <span style={{ color: '#52c41a' }}>
                            ✔ {completed}
                        </span>
                        {' '}
                        <span style={{ color: '#faad14' }}>
                            ⏳ {processing}
                        </span>
                        {' '}
                        <span style={{ color: '#ff4d4f' }}>
                            ✖ {failed}
                        </span>
                    </div>
                </Tooltip>
            </div>
            );
          }
        },
        {
        title: <span>Upload Date <span><i class="fas fa-sort"></i></span></span>,
        dataIndex: 'created',
        align: 'center',
        width: 140,

        sorter: (a, b) =>
        new Date(a.created).getTime() - new Date(b.created).getTime(),
        defaultSortOrder: 'descend',

        render: d =>
            d
            ? new Date(d).toLocaleString()
            : '-'
        }, 
        {
            title: <span>Completed Date <span><i class="fas fa-sort"></i></span></span>,
            dataIndex: 'last_updated',
            align: 'center',
            width: 140,
            
            sorter: (a, b) =>
            new Date(a.created).getTime() - new Date(b.created).getTime(),
            defaultSortOrder: 'descend',

            render: (d, row) =>
                row.status === 'completed'
                ? new Date(d).toLocaleString()
                : '-'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: 'center',
            width: 85,
            render: s => (
            <span
                style={{
                fontWeight: 600,
                color:
                    s === 'completed'
                    ? '#52c41a'
                    : s === 'processing'
                    ? '#faad14'
                    : '#ff4d4f'
                }}
            >
                {s.toUpperCase()}
            </span>
            )
        },
        {
            title: 'Retries',
            dataIndex: 'retry_count',
            align: 'center',
            width: 55,
            render: r => r || 0
        },
        {
            title: 'Action',
            align: 'center',
            width: 50,
            render: (_, row) =>
            row.status === 'completed' ? (
            // <Button
            //     size="small"
            //     className={processBtnCSS}
            //     onClick={() => this.downloadOutput(row.id)}
            // >
            //     Download
            // </Button>

            <Tooltip title="Download File">
                <span
                    onClick={() => this.downloadOutput(row.id)}
                    style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#52c41a',
                    color: '#1890ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                    }}
                >
                    <DownloadOutlined style={{ fontSize: 16 }} />
                </span>
            </Tooltip>
            ) : (
                <span>-</span>
            )
        }
        ];

        return (
            <div>
                <Navigation />
                {/* <div>
                    <nav className="navbar navbar-expand-md navbar-dark navbar-fixed-top" >
                        <div style={{ float: 'left' }}>
                            <img src="Leads-Nexus.png" alt="Leads Nexus" height="50px" width="200px"></img>
                        </div>
                    </nav>
                </div> */}
                <div className="container-fluid" style={{ paddingTop: '100px', paddingBottom: '5%' }}>
                    <div className="row">
                        <div className="col-lg-12" align="center">
                            <label style={{ fontSize: '28px', fontWeight: '500', color: '#193D8F' }}>Mobile Number Verifier</label>
                        </div>
                    </div>

                    <div className="card mt-3">
                        <div className="card-body">

                            <div className="row">

                            {/* LEFT SIDE - Upload */}
                            <div className="col-md-6">
                                {/* Job Type Selection */}
                                <div className="mb-3">
                                    <label style={{color: '#28166f' }}>Select Job Type</label><br />
                                    <Radio.Group
                                        value={this.state.jobType}
                                        onChange={(e) => {
                                            const jobType = e.target.value;
                                            
                                            this.setState({
                                                jobType: jobType,
                                                uploadError: null,
                                                previewData: [],
                                                totalRecords: 0,
                                                validRecords: 0,
                                                invalidRecords: 0,
                                                file: null
                                                });
                                                this.fetchQuota(jobType, this.state.totalRecords);
                                            }}
                                    >
                                        <Radio value="enrichMobile">Enrich Mobile & Verify Employment</Radio>
                                        <Radio value="verifyEmployment">Verify Employment</Radio>
                                    </Radio.Group>
                                </div>

                                {/* File Upload */}
                                <div className="mb-3">
                                    {/* <medium style={{ color: "#555" }}>
                                        {   JOB_REQUIREMENTS[this.state.jobType].requiredMessage    }
                                    </medium> */}
                                    <medium>
                                        <span style={{ color: '#d93025', fontWeight: 600 }}>
                                            Mandatory :
                                        </span>{' '}
                                        {JOB_REQUIREMENTS[this.state.jobType].requiredMessage}
                                    </medium>

                                    <div style={{ marginTop: "10px" }}>
                                        <Upload beforeUpload={this.handleFileUpload} accept=".xlsx,.xls" showUploadList={false}>
                                            <Button icon={<UploadOutlined />}>Upload Excel</Button>
                                        </Upload>
                                    </div>

                                    {/* Show uploaded file name */}
                                    {this.state.file && (
                                        <div style={{ marginTop: "8px", fontStyle: "italic", color: "#007cc3" }}>
                                            Uploaded File: <b>{this.state.file.name}</b>
                                        </div>
                                    )}
                                </div>

                                {/* Error */}
                                {this.state.uploadError && <Alert type="error" message={this.state.uploadError} />}

                                {this.state.totalRecords > 0 && (
                                    <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                                        <Alert
                                            type="info"
                                            message={`Total Records : ${this.state.totalRecords}, Valid : ${this.state.validRecords}, Invalid : ${this.state.invalidRecords}`}
                                            style={{ marginBottom: 0 }}
                                        />
                                        
                                        {this.state.invalidRecords > 0 && (
                                            <span style={{ color: "red", marginLeft: "1rem"}}>
                                                Please fix invalid records before processing.
                                            </span>
                                        )}
                                    </div>
                                )}

                                {this.state.totalRecords > 0 && (
                                    <div className="mt-4 d-flex gap-3 flex-wrap">

                                        {/* AUDIT FILE REPORT — only when invalid rows exist */}
                                        {this.state.invalidRecords > 0 && (
                                            <div className="col-4">
                                                <Button 
                                                    type="default" 
                                                    block
                                                    onClick={this.handleAuditReport}
                                                    className={auditBtnCSS}
                                                >
                                                    Audit File Report
                                                </Button>
                                            </div>
                                        )}

                                        {/* PROCESS WITH MODIGIE — always visible, disabled if invalid > 0 */}
                                        <div className="col-4">
                                            <Button
                                                block
                                                disabled={
                                                    this.state.invalidRecords > 0 ||
                                                    this.state.processingFileName === this.state.file?.name
                                                }
                                                onClick={this.handleProcess}
                                                className={
                                                    this.state.invalidRecords > 0 || 
                                                    this.state.processingFileName === this.state.file?.name
                                                    ? processBtnDisabledCSS
                                                    : processBtnCSS
                                                }
                                            >
                                                Process with Modigie
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {/* </div> */}
                            </div>

                            {/* RIGHT SIDE - Quota Panel */}
                            <div className="col-md-6">
                                {/* <div className={dashboardcard}> */}
                                {this.state.quota && (

                                <div style={{
                                    border: "1px solid #e6f7ff",
                                    padding: "15px",
                                    borderRadius: "6px",
                                    background: "#f6fbff"
                                }}>

                                <h4 style={{ marginBottom: "10px",color: '#28166f' }}>
                                Quota Remaining Status
                                </h4>

                                <p style={{color:"#666"}}>
                                Job Type: <b>{this.getJobTypeLabel(this.state.jobType)}</b>
                                </p>

                                <div className="row">
                                    <div className="col-3">Past 1 minute :</div>
                                    <div className="col-3 text-end">
                                        <b>{this.state.quota.remaining.minute}</b>
                                    </div>

                                    <div className="col-3">Past 10 minutes :</div>
                                    <div className="col-3 text-end">
                                        <b>{this.state.quota.remaining.tenMinutes}</b>
                                    </div>

                                    <div className="col-3">Past 1 hour :</div>
                                    <div className="col-3 text-end">
                                        <b>{this.state.quota.remaining.hour}</b>
                                    </div>
                                </div>

                                <hr/>

                                <div className="row">

                                {/* <div className="col-md-6"> */}
                                <div className="col-3">Past 1 day :</div>
                                    <div className="col-3 text-end">
                                        <b>{this.state.quota.remaining.day}</b>
                                    </div>

                                    <div className="col-3">Past 7 days :</div>
                                    <div className="col-3 text-end">
                                        <b>{this.state.quota.remaining.week}</b>
                                    </div>

                                    <div className="col-3">Past 30 days :</div>
                                    <div className="col-3 text-end">
                                        <b style={{color:"#1890ff"}}>{this.state.quota.remaining.month}</b>
                                    </div>
                                </div>

                                {/* </div> */}

                                </div>

                                )}
                                {/* </div> */}
                            </div>

                            </div>

                            {/* {this.state.lastFileName && (
                            <div className={lastBatchCardCSS} style={{ marginTop: "1rem"}}>
                                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#007cc3' }}>
                                Previous Batch Details
                                </div>

                                <div className="row ">
                                    <div className="col-md-4">
                                        <span className="label">File :</span>
                                        
                                        <Tooltip title={this.state.lastFileName}>
                                            <span className={`${ellipsisText} value`}>
                                                {this.state.lastFileName}
                                            </span>
                                        </Tooltip>
                                    </div>

                                    <div className="col-md-4">
                                        <span className="label">Job Type :</span>
                                        <span className="value">
                                        {this.state.lastJobType === 'verifyEmployment'
                                            ? 'Verify Employment'
                                            : 'Enrich Mobile & Verify Employment'}
                                        </span>
                                    </div>

                                    <div className="col-md-2">
                                        <span className="label">Health :</span>
                                        <Tooltip title={HEALTH_TOOLTIPS[batchHealth] || "Unknown system state"}>
                                        <span 
                                        style={{
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            color:
                                            batchHealth === 'HEALTHY'
                                                ? '#52c41a'      // Green
                                                : batchHealth === 'PROCESSING'
                                                ? '#1890ff'      // Blue (normal running)
                                                : batchHealth === 'RECOVERING'
                                                ? '#faad14'      // Orange (attention)
                                                : batchHealth === 'DELAYED'
                                                ? '#fadb14'      // Yellow (warning)
                                                : batchHealth === 'CRITICAL'
                                                ? '#ff4d4f'      // Red (serious issue)
                                                : '#8c8c8c'
                                            }}
                                        >{batchHealth}</span>
                                        </Tooltip>
                                    </div>
                                </div>
                                <div className="row ">
                                    
                                    <div className="col-md-4">
                                        <span className="label">Records :</span>
                                        <span className="value">{this.state.lastRecordCount}</span>
                                    </div>

                                    <div className="col-md-4">
                                        <span className="label">Jobs :</span>
                                        <span className="value">
                                            ✔ {completed_jobs} | ⏳ {processing_jobs} | ✖ {failed_jobs}
                                        </span>
                                    </div>

                                    <div className="col-md-2">
                                        <span className="label">Status :</span>
                                        <span
                                        className="status"
                                        style={{
                                            color:
                                            this.state.batchStatus === 'completed'
                                                ? '#52c41a'
                                                : this.state.batchStatus === 'processing'
                                                ? '#faad14'
                                                : this.state.batchStatus === 'recovering'
                                                ? '#1890ff'
                                                : '#ff4d4f'
                                        }}
                                        >
                                        {this.state.batchStatus?.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            )} */}

                            <div
                                style={{
                                    marginTop: 25,
                                    padding: 16,
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 6,
                                    // background: '#fafafa',
                                }}
                            >

                                <h4 style={{ marginBottom: 12,color: '#28166f' }}>
                                    Historical Enriched Data Download
                                </h4>

                                <div style={{ display: 'flex', gap: 10, alignItems: 'center'}}>

                                    <DatePicker
                                        placeholder="From Date"
                                        onChange={(d) =>
                                            this.setState({ historyExportFrom: d })
                                        }
                                    />

                                    <DatePicker
                                        placeholder="To Date"
                                        onChange={(d) =>
                                            this.setState({ historyExportTo: d })
                                        }
                                    />

                                    {/* <Button
                                        type="primary"
                                        // loading={this.state.historyExportLoading}
                                        onClick={this.downloadHistoryExport}
                                    >
                                        Download
                                    </Button> */}
                                    <Tooltip title="Download File">
                                        <span
                                            onClick={this.downloadHistoryExport}
                                            style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            background: '#52c41a',
                                            color: '#1890ff',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                            }}
                                        >
                                            <DownloadOutlined style={{ fontSize: 16 }} />
                                        </span>
                                    </Tooltip>

                                </div>

                                <div style={{ marginTop: 6, fontSize: 14, color: '#28166f' }}>
                                    Only completed records with enriched mobile will be exported.
                                </div>
                            </div>

                            {this.state.batchHistory.length > 0 && (
                            <div style={{ marginTop: "1rem"}} className={historyTableCSS}>
                            <h4 style={{ marginBottom: 12, color: '#28166f' }}>
                                Batch History
                            </h4>

                            <Table
                                rowKey="id"
                                columns={historyColumns}
                                dataSource={this.state.batchHistory}
                                pagination={{ pageSize: 10 }}
                                bordered
                                size="small"
                            />
                            </div>
                            )}

                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }
}

// Modigie.propTypes = {
//     logoutUser: PropTypes.func.isRequired,
//     auth: PropTypes.object.isRequired
// }

// const mapStateToProps = (state) => ({ auth: state.auth })
Modigie.propTypes = {
    logoutUser: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});
export default connect(mapStateToProps, { logoutUser })(
    withRouter(Modigie)
);

// export default Modigie;
